import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Typography,
    TextField,
    CircularProgress,
    Alert,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Hidden,
} from '@mui/material';
import { DeleteOutlined, Edit, Close } from '@mui/icons-material';
import ConfirmationDialog from '../../../components/confirm-dialog';
import {
    fetchMyBookStart,
    removeFromMyBookStart,
    updateNotesStart,
} from '../../../store/slices/myBookSlice';
import { RootState } from '../../../store';
import './my-book.css';

interface MyBookEntry {
    id: number;
    userId: number;
    profileId: number;
    notes: string;
    savedAt: string;
    createdAt: string;
    updatedAt: string;
}

const MyBook: React.FC = () => {
    const dispatch = useDispatch();
    const { items, loading, error, currentPage, pageSize, total } = useSelector(
        (state: RootState) => state.myBook
    );

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [editNotes, setEditNotes] = useState('');
    const [editProfileId, setEditProfileId] = useState<number | null>(null);

    useEffect(() => {
        dispatch(fetchMyBookStart({ page: currentPage, size: pageSize } as any));
    }, [dispatch, currentPage, pageSize]);

    const handleRemove = (profileId: number) => {
        setSelectedProfileId(profileId);
        setConfirmOpen(true);
    };

    const confirmRemove = () => {
        if (selectedProfileId) {
            dispatch(removeFromMyBookStart({ profileId: selectedProfileId } as any));
        }
        setConfirmOpen(false);
        setSelectedProfileId(null);
    };

    const handleEditNotes = (entry: MyBookEntry) => {
        setEditProfileId(entry.profileId);
        setEditNotes(entry.notes);
        setEditOpen(true);
    };

    const saveNotes = () => {
        if (editProfileId) {
            dispatch(
                updateNotesStart({
                    profileId: editProfileId,
                    notes: editNotes,
                } as any)
            );
        }
        setEditOpen(false);
        setEditProfileId(null);
        setEditNotes('');
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        dispatch(fetchMyBookStart({ page: value - 1, size: pageSize } as any));
    };

    const totalPages = Math.ceil(total / pageSize);

    return (
        <>
            <Hidden mdUp>
                <Box sx={{ height: "50px" }} />
            </Hidden>
            <Box sx={{ p: 3 }}>
                <Container maxWidth="lg">
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                            My Book
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Your collection of saved profiles
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading && items.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : items.length === 0 ? (
                        <Card sx={{ textAlign: 'center', py: 8 }}>
                            <Typography variant="h6" color="textSecondary">
                                No saved profiles yet
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Start saving your favorite profiles to your My Book collection
                            </Typography>
                        </Card>
                    ) : (
                        <>
                            <Grid container spacing={3}>
                                {items.map((entry: MyBookEntry) => (
                                    <Grid item xs={12} sm={6} md={4} key={entry.id}>
                                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                            <CardContent sx={{ pb: 1, flexGrow: 1 }}>
                                                <Typography
                                                    color="primary"
                                                    sx={{
                                                        fontWeight: 600,
                                                        mb: 1,
                                                    }}
                                                >
                                                    Profile #{entry.profileId}
                                                </Typography>

                                                <Typography variant="caption" color="textSecondary">
                                                    Saved: {new Date(entry.savedAt).toLocaleDateString()}
                                                </Typography>

                                                {entry.notes && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                            <strong>Notes:</strong>
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                            sx={{
                                                                p: 1,
                                                                bgcolor: '#f5f5f5',
                                                                borderRadius: 1,
                                                                maxHeight: '80px',
                                                                overflow: 'auto',
                                                            }}
                                                        >
                                                            {entry.notes}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </CardContent>

                                            <CardActions sx={{ pt: 0 }}>
                                                <Button
                                                    size="small"
                                                    startIcon={<Edit />}
                                                    onClick={() => handleEditNotes(entry)}
                                                    sx={{ color: '#1976d2' }}
                                                >
                                                    Notes
                                                </Button>
                                                <Button
                                                    size="small"
                                                    startIcon={<DeleteOutlined />}
                                                    onClick={() => handleRemove(entry.profileId)}
                                                    sx={{ color: '#d32f2f' }}
                                                >
                                                    Remove
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>

                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage + 1}
                                        onChange={handlePageChange}
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Container>
            </Box>

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirmRemove}
                title="Remove from My Book"
                message="Are you sure you want to remove this profile from your My Book?"
                confirmText="Remove"
                confirmColor="error"
                cancelText="Cancel"
            />

            {/* Edit Notes Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Notes</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Add personal notes about this profile..."
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button onClick={saveNotes} variant="contained" color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MyBook;
